// General Invoice Payment by Cash — mirrors the CashAccess form of legacy /Accounts/GeneralInvoicePayment
// (Sl-erp-ui Views/Accounts/GeneralInvoicePayment.cshtml): same field order, cascades and validations.
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Receipt, Loader2, RotateCcw, Send, ChevronDown, CheckCircle2, AlertCircle, FileText } from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchSelfCCList,
    fetchOtherCCList,
    fetchCCBalance,
    clearDCAAndBelow,
    clearOtherCCList,
    resetCashVoucher,
    selectSelfCCList,
    selectOtherCCList,
    selectCCBalance,
    selectSelfCCLoading,
    selectOtherCCLoading,
    selectCCBalanceLoading,
} from '../../slices/accountsSlice/cashVoucherSlice';

import {
    fetchGenInvList,
    fetchGenInvDetail,
    submitGenInvCashPayment,
    clearInvoiceDetail,
    clearSaveResult,
    resetGenInvPayment,
    selectGenInvList,
    selectGenInvDetail,
    selectGenInvListLoading,
    selectGenInvDetailLoading,
    selectGenInvSaveLoading,
} from '../../slices/accountsSlice/generalInvoicePaymentSlice';

import { getDCAByCC, getSubDCAByDCA } from '../../api/AccountsAPI/generalInvoicePaymentAPI';

// ─── Constants / helpers ──────────────────────────────────────────────────────
// Legacy DebitTypeList values; spInsertGenInvCashPayment gets the TransactionMode text below
const DEBIT_SELF  = 'SelfCC';
const DEBIT_OTHER = 'OtherCC';
const DEBIT_OPTIONS = [
    { v: DEBIT_SELF,  l: 'Self Debit',            mode: 'Self Debit' },
    { v: DEBIT_OTHER, l: 'Debit From Other CC',   mode: 'Debit From Other CC' },
];

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const today = () => { const d = new Date(); d.setHours(12, 0, 0, 0); return d; };
// Legacy datepicker format 'dd-M-yy' → 15-Oct-2026
const toDMonY = (d) => {
    if (!d) return '';
    const x = new Date(d);
    return `${String(x.getDate()).padStart(2, '0')}-${MONTH_ABBR[x.getMonth()]}-${x.getFullYear()}`;
};
const fmt = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
// Legacy ValidateAmount: digits with at most 2 decimals
const isAmountInput = (v) => /^\d*\.?\d{0,2}$/.test(v);
const num = (v) => parseFloat(v) || 0;
const unwrap = (res) => (Array.isArray(res) ? res : res?.Data || []);

const inputCls =
    'w-full h-9 px-2.5 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-900/40 focus:border-indigo-500 disabled:bg-gray-100 dark:disabled:bg-gray-700/60 disabled:cursor-not-allowed';
const selectCls = `${inputCls} appearance-none pr-8 cursor-pointer`;
const readOnlyCls = `${inputCls} bg-gray-100 dark:bg-gray-700/60 font-semibold`;

const CardHeader = ({ icon: Icon, title }) => (
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2 bg-gray-50/60 dark:bg-gray-900/40 rounded-t-xl">
        <Icon className="h-4 w-4 text-indigo-500" />
        <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
    </div>
);

// Summary panel row
const SumRow = ({ label, value }) => (
    <div className="flex justify-between gap-3">
        <dt className="text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</dt>
        <dd className="font-semibold text-gray-800 dark:text-gray-100 truncate text-right" title={value || ''}>{value || '—'}</dd>
    </div>
);

const Field = ({ label, required, className = '', children }) => (
    <div className={`min-w-0 ${className}`}>
        <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
            {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
        </label>
        {children}
    </div>
);

const Select = ({ value, onChange, disabled, loading, placeholder = '-Please Select-', options, getValue, getLabel }) => (
    <div className="relative">
        <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled || loading} className={selectCls}>
            <option value="">{loading ? 'Loading...' : placeholder}</option>
            {options.map((o, idx) => (
                <option key={getValue(o) || idx} value={getValue(o)}>{getLabel(o)}</option>
            ))}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
            {loading ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
    </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const GeneralInvoiceCashPayment = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const roleId   = userData?.roleId   || userData?.RID  || 0;
    const userId   = userData?.userId   || userData?.UID  || userData?.employeeId || '';
    const userName = userData?.userName || userData?.UserName || 'User';

    const selfCCList       = useSelector(selectSelfCCList);
    const otherCCList      = useSelector(selectOtherCCList);
    const ccBalance        = useSelector(selectCCBalance);
    const selfCCLoading    = useSelector(selectSelfCCLoading);
    const otherCCLoading   = useSelector(selectOtherCCLoading);
    const ccBalanceLoading = useSelector(selectCCBalanceLoading);
    const invoiceList      = useSelector(selectGenInvList);
    const invoice          = useSelector(selectGenInvDetail);
    const invListLoading   = useSelector(selectGenInvListLoading);
    const invDetailLoading = useSelector(selectGenInvDetailLoading);
    const saveLoading      = useSelector(selectGenInvSaveLoading);

    // ── Form state (legacy order) ─────────────────────────────────────────────
    const [selfCC,     setSelfCC]     = useState('');
    const [debitType,  setDebitType]  = useState('');   // '' | DEBIT_SELF | DEBIT_OTHER
    const [otherCC,    setOtherCC]    = useState('');
    const [invoiceNo,  setInvoiceNo]  = useState('');
    const [dca,        setDca]        = useState('');
    const [sdca,       setSdca]       = useState('');
    const [paidDate,   setPaidDate]   = useState(today);
    const [paidAmount, setPaidAmount] = useState('');
    const [remarks,    setRemarks]    = useState('');

    const [dcaList,     setDcaList]     = useState([]);
    const [sdcaList,    setSdcaList]    = useState([]);
    const [dcaLoading,  setDcaLoading]  = useState(false);
    const [sdcaLoading, setSdcaLoading] = useState(false);

    // Inline message box like legacy's divInvoiceCreationInfoMsg
    const [message, setMessage] = useState(null); // { type: 'error' | 'success', title, lines: [] }

    // CC whose invoices / account heads are used
    const debitCC = debitType === DEBIT_OTHER ? otherCC : debitType === DEBIT_SELF ? selfCC : '';
    const balance = num(invoice?.Balance);

    // ── Mount ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (userId && roleId) dispatch(fetchSelfCCList({ uid: userId, rid: roleId }));
        return () => {
            dispatch(resetCashVoucher());
            dispatch(resetGenInvPayment());
        };
    }, [dispatch, userId, roleId]);

    // Invoices of the debit CC (legacy BindOtherCCCodes / BindInvoices → GetGeneralInvoice)
    useEffect(() => {
        if (debitCC) dispatch(fetchGenInvList(debitCC));
    }, [dispatch, debitCC]);

    // Account heads of the debit CC once an invoice is picked (legacy InvoiceNoChange → BindDCA)
    useEffect(() => {
        if (!invoiceNo || !debitCC) return;
        let cancelled = false;
        setDcaLoading(true);
        getDCAByCC(debitCC)
            .then((res) => { if (!cancelled) setDcaList(unwrap(res)); })
            .catch(() => { if (!cancelled) { setDcaList([]); toast.error('Failed to load account heads'); } })
            .finally(() => { if (!cancelled) setDcaLoading(false); });
        return () => { cancelled = true; };
    }, [invoiceNo, debitCC]);

    // ── Cascades ──────────────────────────────────────────────────────────────
    const clearInvoiceAndBelow = () => {
        setInvoiceNo(''); setDca(''); setSdca(''); setPaidAmount('');
        setDcaList([]); setSdcaList([]);
        dispatch(clearInvoiceDetail());
    };

    const handleSelfCCChange = (val) => {
        setSelfCC(val);
        setDebitType(''); setOtherCC('');
        clearInvoiceAndBelow();
        dispatch(clearDCAAndBelow()); // also clears the old balance
        dispatch(clearOtherCCList());
        if (val) dispatch(fetchCCBalance(val));
    };

    const handleDebitTypeChange = (val) => {
        setDebitType(val); setOtherCC('');
        clearInvoiceAndBelow();
        dispatch(clearOtherCCList());
        if (val === DEBIT_OTHER && selfCC) dispatch(fetchOtherCCList({ id: selfCC, type: DEBIT_OTHER }));
    };

    const handleOtherCCChange = (val) => {
        setOtherCC(val);
        clearInvoiceAndBelow();
    };

    const handleInvoiceChange = (val) => {
        clearInvoiceAndBelow();
        setInvoiceNo(val);
        if (val) dispatch(fetchGenInvDetail(val));
    };

    const handleDCAChange = (val) => {
        setDca(val); setSdca(''); setSdcaList([]);
        if (!val) return;
        setSdcaLoading(true);
        getSubDCAByDCA(val)
            .then((res) => setSdcaList(unwrap(res)))
            .catch(() => { setSdcaList([]); toast.error('Failed to load sub account heads'); })
            .finally(() => setSdcaLoading(false));
    };

    const resetForm = () => {
        setSelfCC(''); setDebitType(''); setOtherCC('');
        clearInvoiceAndBelow();
        setPaidDate(today()); setRemarks('');
        dispatch(clearDCAAndBelow());
        dispatch(clearOtherCCList());
        dispatch(resetGenInvPayment());
    };

    const handleReset = () => { resetForm(); setMessage(null); };

    // ── Submit (legacy SubmitInvoiceCashPAy validations, same order/wording) ──
    const handleSubmit = async () => {
        const errs = [];
        if (!selfCC)    errs.push('Select Cost Center Code');
        if (!debitType) errs.push('Select Transaction Mode');
        else if (debitType === DEBIT_OTHER && !otherCC) errs.push('Select Other Cost Center Code');
        if (!invoiceNo) errs.push('Select Invoice No');
        if (!dca)       errs.push('Select Account Head');
        if (!sdca)      errs.push('Select SubAccount Head');
        if (!paidDate)  errs.push('Select Transaction Date');
        if (!remarks.trim()) errs.push('Enter Remarks');
        if (num(paidAmount) <= 0) errs.push('Select Paid Amount');
        else if (balance < num(paidAmount)) errs.push('Paid Amount is Greater than Invoice Balance');

        if (errs.length) {
            setMessage({ type: 'error', title: 'Please enter all required fields!', lines: errs });
            return;
        }
        setMessage(null);

        try {
            const res = await dispatch(submitGenInvCashPayment({
                InvoiceNo:         invoiceNo,
                TransactionMode:   DEBIT_OPTIONS.find((o) => o.v === debitType).mode,
                SelfCC:            selfCC,
                OtherCC:           debitType === DEBIT_OTHER ? otherCC : '',
                InvoiceDate:       invoice?.InvoiceDate || '',
                PaidDate:          toDMonY(paidDate),
                CashDCACode:       dca,
                CashSubDca:        sdca,
                Name:              invoice?.Name || '',
                TransactionAmount: num(paidAmount),
                Remarks:           remarks.trim(),
                Createdby:         userName,
                RoleId:            roleId,
            })).unwrap();

            // spInsertGenInvCashPayment returns "Submited$<VoucherId>" on success, otherwise an error text
            const raw   = typeof res?.Data === 'string' ? res.Data : typeof res === 'string' ? res : '';
            const parts = raw.split('$');
            dispatch(clearSaveResult());
            if (parts[0].trim() === 'Submited') {
                const msg = `Voucher Id ${(parts[1] || '').trim()} Created Successfully`;
                toast.success(msg);
                resetForm();
                setMessage({ type: 'success', title: msg, lines: [] });
            } else {
                setMessage({ type: 'error', title: raw || 'Error Occurred While Payment', lines: [] });
            }
        } catch (err) {
            dispatch(clearSaveResult());
            setMessage({ type: 'error', title: (typeof err === 'string' && err) || 'Error Occurred While Payment', lines: [] });
        }
    };

    const busy = saveLoading;

    // Display labels for the summary panel
    const selfCCLabel  = selfCCList.find((o) => String(o.CC_Id) === String(selfCC))?.CC_Code || '';
    const otherCCLabel = otherCCList.find((o) => String(o.CC_Id) === String(otherCC))?.CC_Code || '';
    const dcaLabel     = dcaList.find((o) => String(o.DCACode) === String(dca))?.DCAIDSTR || '';
    const sdcaLabel    = sdcaList.find((o) => String(o.SubDCACode) === String(sdca))?.SubDCAIDSTR || '';
    const debitLabel   = DEBIT_OPTIONS.find((o) => o.v === debitType)?.l || '';
    const exceedsBalance = invoice && num(paidAmount) > 0 && num(paidAmount) > balance;

    // ──────────────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-3 md:p-4">

            {/* ── Page Header (slim) ───────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-md shadow-indigo-500/20 px-5 py-3 text-white flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <Receipt className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-black tracking-tight leading-tight truncate">
                            {menuData?.name || 'General Invoice Payment'}
                        </h1>
                        <p className="text-indigo-200 text-xs">Accounts Module · General Invoice Payment by Cash</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

                {/* ── Left: single form card ───────────────────────────────── */}
                <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <CardHeader icon={Receipt} title="Payment Details" />
                    <div className="p-4 space-y-4">

                        {/* Cost centres */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                            <Field label="Self Cost Center" required>
                                <Select
                                    value={selfCC}
                                    onChange={handleSelfCCChange}
                                    disabled={busy}
                                    loading={selfCCLoading}
                                    options={selfCCList}
                                    getValue={(o) => o.CC_Id}
                                    getLabel={(o) => o.CC_Code}
                                />
                                {selfCC && (
                                    <p className="mt-1 text-[11px] font-semibold text-indigo-700 dark:text-indigo-300 flex items-center gap-1">
                                        Balance:{' '}
                                        {ccBalanceLoading
                                            ? <Loader2 className="h-3 w-3 animate-spin" />
                                            : `₹${fmt(ccBalance ?? 0)}`}
                                    </p>
                                )}
                            </Field>
                            <Field label="Mode of Transaction" required>
                                <Select
                                    value={debitType}
                                    onChange={handleDebitTypeChange}
                                    disabled={busy || !selfCC}
                                    placeholder={selfCC ? '-Please Select-' : 'Select Self CC first'}
                                    options={DEBIT_OPTIONS}
                                    getValue={(o) => o.v}
                                    getLabel={(o) => o.l}
                                />
                            </Field>
                            {debitType === DEBIT_OTHER && (
                                <Field label="Other Cost Center" required>
                                    <Select
                                        value={otherCC}
                                        onChange={handleOtherCCChange}
                                        disabled={busy}
                                        loading={otherCCLoading}
                                        options={otherCCList}
                                        getValue={(o) => o.CC_Id}
                                        getLabel={(o) => o.CC_Code}
                                    />
                                </Field>
                            )}
                        </div>

                        {!debitCC ? (
                            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6 border-t border-gray-100 dark:border-gray-700">
                                Select the cost center and mode of transaction to load invoices.
                            </p>
                        ) : (
                            <>
                                {/* Invoice */}
                                <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <p className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
                                        <FileText className="h-3.5 w-3.5 text-indigo-500" /> Invoice
                                    </p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                                        <Field label="Invoice No" required>
                                            <Select
                                                value={invoiceNo}
                                                onChange={handleInvoiceChange}
                                                disabled={busy}
                                                loading={invListLoading}
                                                placeholder={!invListLoading && invoiceList.length === 0 ? 'No invoices pending' : '-Please Select-'}
                                                options={invoiceList}
                                                getValue={(o) => o.InvoiceNo}
                                                getLabel={(o) => (o.Name ? `${o.InvoiceNo} - ${o.Name}` : o.InvoiceNo)}
                                            />
                                        </Field>
                                        <Field label="Invoice Balance">
                                            <input type="text" readOnly value={invoice ? fmt(invoice.Balance) : ''} className={`${readOnlyCls} text-right`} />
                                        </Field>
                                        <Field label="Invoice Date">
                                            <input type="text" readOnly value={invoice?.InvoiceDate || ''} className={readOnlyCls} />
                                        </Field>
                                        <Field label="Name" className="sm:col-span-2 lg:col-span-3">
                                            <div className="relative">
                                                <input type="text" readOnly value={invoice?.Name || ''} className={readOnlyCls} />
                                                {invDetailLoading && <Loader2 className="absolute right-2 top-2.5 h-4 w-4 text-indigo-500 animate-spin" />}
                                            </div>
                                        </Field>
                                    </div>
                                </div>

                                {/* Heads, date, amount */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <Field label="Account Head" required>
                                        <Select
                                            value={dca}
                                            onChange={handleDCAChange}
                                            disabled={busy || !invoiceNo}
                                            loading={dcaLoading}
                                            placeholder={invoiceNo ? '-Please Select-' : 'Select Invoice first'}
                                            options={dcaList}
                                            getValue={(o) => o.DCACode}
                                            getLabel={(o) => o.DCAIDSTR}
                                        />
                                    </Field>
                                    <Field label="Sub Account Head" required>
                                        <Select
                                            value={sdca}
                                            onChange={setSdca}
                                            disabled={busy || !dca}
                                            loading={sdcaLoading}
                                            options={sdcaList}
                                            getValue={(o) => o.SubDCACode}
                                            getLabel={(o) => o.SubDCAIDSTR}
                                        />
                                    </Field>
                                    <Field label="Paid Date" required>
                                        <CustomDatePicker
                                            value={paidDate}
                                            onChange={setPaidDate}
                                            maxDate={today()}
                                            size="sm"
                                            format="DD/MM/YYYY"
                                            placeholder="Select paid date"
                                            disabled={busy}
                                        />
                                    </Field>
                                </div>

                                {/* Remarks + amount */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <Field label="Remarks" required className="sm:col-span-2">
                                        <input type="text" value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={busy} className={inputCls} />
                                    </Field>
                                    <Field label="Paid Amount" required>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="0.00"
                                            value={paidAmount}
                                            onChange={(e) => { if (isAmountInput(e.target.value)) setPaidAmount(e.target.value); }}
                                            disabled={busy || !invoice}
                                            className={`${inputCls} text-right ${exceedsBalance ? 'border-rose-400 focus:border-rose-500' : ''}`}
                                        />
                                    </Field>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* ── Right: sticky summary + actions ──────────────────────── */}
                <div className="lg:sticky lg:top-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={CheckCircle2} title="Payment Summary" />
                        <div className="p-4 space-y-3">
                            <dl className="space-y-1.5 text-xs">
                                <SumRow label="Self CC"      value={selfCCLabel} />
                                <SumRow label="CC Balance"   value={selfCC ? (ccBalanceLoading ? 'Loading…' : `₹${fmt(ccBalance ?? 0)}`) : ''} />
                                <SumRow label="Mode"         value={debitLabel} />
                                {debitType === DEBIT_OTHER && <SumRow label="Other CC" value={otherCCLabel} />}
                                <SumRow label="Invoice No"   value={invoiceNo} />
                                <SumRow label="Name"         value={invoice?.Name} />
                                <SumRow label="Account Head" value={[dcaLabel, sdcaLabel].filter(Boolean).join(' / ')} />
                                <SumRow label="Paid Date"    value={paidDate ? toDMonY(paidDate) : ''} />
                            </dl>

                            <dl className="space-y-1.5 text-xs pt-3 border-t border-gray-100 dark:border-gray-700">
                                <SumRow label="Invoice Balance" value={invoice ? `₹${fmt(balance)}` : ''} />
                            </dl>

                            <div className={`px-4 py-3 rounded-lg text-white bg-gradient-to-r ${exceedsBalance ? 'from-amber-500 to-orange-600' : 'from-indigo-500 to-violet-600'}`}>
                                <p className="text-white/80 text-[10px] font-semibold uppercase tracking-wider">Paid Amount</p>
                                <p className="text-2xl font-black">₹{fmt(num(paidAmount))}</p>
                                {exceedsBalance && <p className="text-[11px] text-white/90">More than the invoice balance</p>}
                            </div>

                            {/* Message box (validation errors / save result) */}
                            {message && (
                                <div className={`rounded-lg border px-3 py-2 text-xs ${message.type === 'success'
                                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 text-green-800 dark:text-green-300'
                                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-700 dark:text-red-300'}`}>
                                    <p className="font-semibold flex items-start gap-1.5">
                                        {message.type === 'success'
                                            ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                            : <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />}
                                        <span className="break-words">{message.title}</span>
                                    </p>
                                    {message.lines.length > 0 && (
                                        <ul className="mt-1 ml-5 list-disc space-y-0.5">
                                            {message.lines.map((l) => <li key={l}>{l}</li>)}
                                        </ul>
                                    )}
                                </div>
                            )}

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handleReset}
                                    disabled={busy}
                                    className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                                >
                                    <RotateCcw className="h-4 w-4" /> Reset
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={busy || !invoiceNo}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                    {busy ? 'Saving...' : 'Submit Payment'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GeneralInvoiceCashPayment;
